import { LocalizationCode, LocalizationCodeValues } from '../types/LocalizationCode';
import { CommonConstants } from './CommonConstants';
import { GetLocaleData } from './GetLocaleData';
import type { IUsageMeterJob } from '../contracts/IUsageMeterJob';
import type { SharedSettingsMap } from './GetSharedSettings';

/** File-scope constants for the shared common helpers. */
const localConstants = {
  // Usage-meter idempotency-key segment separator, and the BullMQ-safe job-id separator (BullMQ rejects ':').
  usageKeySeparator: ':',
  jobIDSeparator: '-',
  // One billable unit per delivered notification, discriminated as a notification-sourced meter event.
  notificationUsageQuantity: 1,
  notificationUsageSource: 'notification',
  // One billable unit per received customer text, discriminated as a text-message-sourced meter event.
  textMessageUsageSource: 'text_message',
  // Single canonical date/time display contract shared by every frontend surface (lobby dashboard + admin portal).
  timestampEmptyPlaceholder: '—',
  timestampOptions: { dateStyle: 'medium', timeStyle: 'short' },
  // Duration (m:ss) rendering: value for absent/zero/negative input, plus the seconds-field pad width and char.
  durationZero: '0:00',
  durationSecondsPadWidth: 2,
  durationPadChar: '0',
  // E.164 phone format: leading '+', a non-zero leading digit, then up to 14 further digits (15 digits max).
  e164Pattern: /^\+[1-9]\d{1,14}$/,
  // Phone normalization: characters stripped before applying a country code, the NANP default country code and national-number length, and the '00' international dialling prefix.
  phoneNonDigitPattern: /[^\d]/g,
  phoneDefaultCountryCode: '1',
  phoneNanpNationalDigits: 10,
  phoneInternationalPrefix: '00',
  // Text normalization: any run of whitespace (incl. line breaks) collapsed to one space; end-of-line variants unified; trailing per-line whitespace stripped.
  anyWhitespaceRunPattern: /\s+/g,
  // Angle brackets are the only markup-bearing characters stripped from calendar-bound free text. Quotes and
  // apostrophes are kept: real names contain them and they carry no markup meaning.
  calendarMarkupCharsPattern: /[<>]/g,
  // Log redaction: the mask glyph, and how many trailing phone digits survive it (enough to correlate, not enough to dial).
  redactionChar: '*',
  redactedPhoneTailDigits: 4,
  eolPattern: /\r\n?/g,
  trailingWhitespacePattern: /[ \t]+$/,
  // Pragmatic email shape: a non-space local part, '@', and a dotted domain. Format gate only - delivery is the real test.
  emailPattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  // Scheme prepended when validating a scheme-less website URL (users routinely type "example.com").
  urlDefaultScheme: 'https://',
  // A plausible website hostname contains at least one dot and no spaces (URL parsing alone accepts single-label hosts like "localhost").
  urlHostPattern: /^[^\s]+\.[^\s]+$/,
  // Canonical 8-4-4-4-12 hex UUID shape, lenient on version/variant bits to match what PostgreSQL's uuid type accepts.
  uuidPattern: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  // ISO-8601 combined date-time: YYYY-MM-DD, 'T' separator, HH:mm with optional :ss and fractional seconds, and an OPTIONAL zone designator (Z or +/-HH:mm). Anchored because `Date.parse` also
  // accepts implementation-defined formats ('Dec 25 2026', '2026/07/02', '2026-07-02 09:00') whose interpretation varies by engine. The zone stays optional on purpose: the voice tool contract
  // (VoiceToolSchemas) requires offset-less business-local wall-clock times, which the controller anchors to the tenant timezone.
  iso8601Pattern: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,9})?)?(Z|[+-]\d{2}:\d{2})?$/,
  // Leading characters a spreadsheet interprets as a formula. Neutralised before a value is written into a CSV cell.
  csvFormulaPrefixPattern: /^[=+\-@\t\r]/,
  // Untrusted-map sanitization: Mongo operator-key prefix, nested-path char, and prototype-pollution sentinel keys.
  mongoOperatorKeyPrefix: '$',
  mongoDottedPathChar: '.',
  forbiddenObjectKeys: ['__proto__', 'constructor', 'prototype'],
  // Sender attribution: the platform attribution prefix, plus the pattern that suppresses the redundant \"on behalf of <business>\" clause when the name already contains \"Echo\" (any casing).
  platformAttribution: 'From Vertex Echo',
  platformNamePattern: /echo/i,
  // Unicode bidi isolates used by WrapLTR: LRI (Left-to-Right Isolate) opens a left-to-right run; PDI (Pop Directional Isolate) closes it. Built via fromCharCode so no raw control glyph sits in the
  // source. Wrapping a value keeps phone numbers, IDs, and Latin-script names in natural order inside an RTL (Arabic/Hebrew) paragraph instead of visually reversing.
  leftToRightIsolate: String.fromCharCode(0x2066),
  popDirectionalIsolate: String.fromCharCode(0x2069)
} as const;

/** Ambient display locale for frontend date/time formatting, set by each app via SetDisplayLocale. Defaults to the product default so backend and test formatting stay deterministic. */
let displayLocale: string = CommonConstants.Defaults.Localization;

/**
 * Sets the ambient display locale used by FormatTimestamp. Frontends call this whenever the user's selected language changes so date/time output follows the in-app choice rather than the browser
 * locale. Pass undefined to revert to the product default (CommonConstants.Defaults.Localization).
 *
 * @param locale - A BCP-47 LocalizationCode (e.g. 'en-US', 'es-ES', 'ar-SA'), or undefined for the product default.
 */
export function SetDisplayLocale(locale: string | undefined): void {
  displayLocale = locale ?? CommonConstants.Defaults.Localization;
}

/**
 * Replaces localhost references with the provided client IP in Development mode.
 *
 * @param settings - The settings object to mutate.
 * @param clientIp - The client IP address to use instead of localhost.
 */
export function ReplaceLocalhostWithIp(settings: SharedSettingsMap, clientIp: string): void {
  if (!clientIp || clientIp === 'localhost' || clientIp === '127.0.0.1') return;

  Object.keys(settings).forEach((key) => {
    const value = settings[key as keyof SharedSettingsMap];
    if (typeof value === 'string' && value.includes('localhost:')) {
      (settings[key as keyof SharedSettingsMap] as string) = value.replace(/localhost:/g, `${clientIp}:`);
    }
  });
}

/**
 * Formats a timestamp value (Date, epoch milliseconds, or ISO string) into the single canonical app date-time format (medium date, short time). Absent or unparseable values render as an em dash.
 *
 * Uses the ambient display locale (set via SetDisplayLocale, defaulting to the product default) so output follows the user's in-app language choice. The single source of truth for timestamp
 * display - used by the frontends AND by backend notifications (owner alerts, caller confirmations) so every surface renders the same readable format.
 *
 * @param value - The timestamp value, possibly null or undefined.
 * @param timeZone - Optional IANA timezone (e.g. 'America/New_York') to render the instant in. Omit to use the runtime's local zone (the frontend default: the user's own browser zone). Backend
 *   callers pass the tenant's timezone so an owner/caller sees the business-local time rather than a raw UTC instant. An invalid zone falls back to the runtime local zone rather than throwing.
 * @param locale - Optional BCP-47 locale (e.g. 'es-ES') to format the date/time in. Omit to use the ambient display locale (`SetDisplayLocale`, the frontend default). Backend callers pass the
 *   tenant's `defaultLocale` so a localized notification renders its date in the recipient's language. An invalid locale falls back to the ambient locale rather than throwing.
 * @returns A human-readable date-time string, or the em-dash placeholder.
 */
export function FormatTimestamp(value: Date | number | string | null | undefined, timeZone?: string, locale?: string): string {
  if (value === null || value === undefined) return localConstants.timestampEmptyPlaceholder;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return localConstants.timestampEmptyPlaceholder;
  const resolvedLocale = locale ?? displayLocale;
  const options = timeZone ? { ...localConstants.timestampOptions, timeZone } : localConstants.timestampOptions;
  try {
    return date.toLocaleString(resolvedLocale, options);
  } catch {
    // An invalid/unsupported locale or IANA zone makes toLocaleString throw a RangeError; fall back to the ambient locale in the runtime-local zone rather than failing the render/notification.
    try {
      return date.toLocaleString(displayLocale, localConstants.timestampOptions);
    } catch {
      return date.toISOString();
    }
  }
}

/**
 * Converts an instant (Date, epoch ms, or ISO string) into a bare `YYYY-MM-DDTHH:mm:ss` wall-clock string as observed in the given IANA timezone (no offset suffix). Used to author
 * calendar-provider events in the tenant's business timezone: an event created with this local wall-clock plus its IANA `timeZone` renders as the business-local time in Google/Outlook rather
 * than a UTC clock the viewer must mentally convert (which is what makes Outlook show one time in the month grid and another in the opened event). The instant is preserved - only its textual
 * representation changes.
 *
 * @param value - The instant to convert.
 * @param timeZone - The target IANA timezone (e.g. 'America/New_York').
 * @returns The zoned wall-clock string, or null when the value is unparseable or the timezone is invalid/unsupported (caller should fall back to a UTC representation).
 */
export function ToZonedWallClock(value: Date | number | string, timeZone: string): string | null {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).formatToParts(date);
    const get = (type: string): string => parts.find((part) => part.type === type)?.value ?? '';
    // Some engines emit '24' for midnight under hour12:false; normalise it to '00' so the string stays a valid time.
    const hour = get('hour') === '24' ? '00' : get('hour');
    return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}:${get('second')}`;
  } catch {
    // An invalid/unsupported IANA zone makes Intl throw a RangeError; signal the caller to fall back rather than crashing the booking.
    return null;
  }
}

/**
 * Renders an ISO 3166-1 alpha-2 country code for prompt display as `Name (CODE)` (e.g. `Canada (CA)`). A bare `CA` in an address line reads as California to a model; the explicit country name
 * kills that confusion even when the country is the ONLY address part the tenant entered. Falls back to the uppercased code itself for anything Intl cannot name.
 *
 * @param countryCode - The ISO alpha-2 country code (any case), possibly empty.
 * @returns The display string, or an empty string when no code was given.
 */
export function FormatCountryForPrompt(countryCode: string): string {
  const code = countryCode.trim().toUpperCase();
  if (!code) return '';
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'region' }).of(code);
    return name && name !== code ? `${name} (${code})` : code;
  } catch {
    // A malformed code (not alpha-2) makes Intl throw a RangeError; show the raw value rather than dropping the tenant's data.
    return code;
  }
}

/**
 * Computes how many minutes ahead of UTC the given IANA timezone is at a specific instant (accounting for DST). Positive east of UTC, negative west.
 *
 * @param timeZone - The IANA timezone (e.g. 'America/Los_Angeles').
 * @param atMs - The instant (epoch ms) at which to evaluate the offset.
 * @returns The offset in minutes, or null when the timezone is invalid/unsupported.
 */
function TimeZoneOffsetMinutes(timeZone: string, atMs: number): number | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).formatToParts(new Date(atMs));
    const get = (type: string): string => parts.find((part) => part.type === type)?.value ?? '';
    const hour = get('hour') === '24' ? '00' : get('hour');
    const asUTC = Date.UTC(Number(get('year')), Number(get('month')) - 1, Number(get('day')), Number(hour), Number(get('minute')), Number(get('second')));
    return Math.round((asUTC - atMs) / (CommonConstants.MsPerSecond * CommonConstants.SecPerMinute));
  } catch {
    return null;
  }
}

/**
 * Interprets a date-time as a WALL-CLOCK reading in the given IANA timezone and returns the corresponding absolute instant as a UTC ISO 8601 string (with `Z`). Any timezone offset the input
 * carries (a trailing `Z` or `±HH:MM`) is IGNORED - only the year/month/day/hour/minute[/second] digits are read - so a value the caller states in business-local time is anchored correctly even
 * when an upstream producer (e.g. an LLM tool call) mislabels a local time as UTC. This is the inverse of {@link ToZonedWallClock}.
 *
 * @param value - A date-time string whose wall-clock digits are the local time (e.g. '2026-07-02T09:00:00' or, tolerated, '2026-07-02T09:00:00Z').
 * @param timeZone - The IANA timezone the wall-clock is expressed in (e.g. 'America/Los_Angeles').
 * @returns The UTC instant as an ISO 8601 string, or null when the value is unparseable or the timezone is invalid/unsupported (caller should fall back).
 */
export function ZonedWallClockToUTC(value: string, timeZone: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(value.trim());
  if (!match) return null;
  const [, year, month, day, hour, minute, second] = match;
  // Treat the wall-clock as if it were UTC to get a provisional instant, then subtract the zone's actual offset at that instant to land on the true UTC moment.
  const naiveAsUTCMs = Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second ?? '0'));
  if (Number.isNaN(naiveAsUTCMs)) return null;
  const offset = TimeZoneOffsetMinutes(timeZone, naiveAsUTCMs);
  if (offset === null) return null;
  const msPerMinute = CommonConstants.MsPerSecond * CommonConstants.SecPerMinute;
  let utcMs = naiveAsUTCMs - offset * msPerMinute;
  // Re-evaluate the offset at the computed instant so a value near a DST transition resolves against the offset actually in effect at the real moment.
  const offsetAtInstant = TimeZoneOffsetMinutes(timeZone, utcMs);
  if (offsetAtInstant !== null && offsetAtInstant !== offset) utcMs = naiveAsUTCMs - offsetAtInstant * msPerMinute;
  return new Date(utcMs).toISOString();
}

/**
 * Formats a duration in seconds as m:ss. Absent, zero, or negative values render as 0:00.
 *
 * @param seconds - The duration in seconds, possibly null or undefined.
 * @returns The formatted m:ss string.
 */
export function FormatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds < 0) return localConstants.durationZero;
  const minutes = Math.floor(seconds / CommonConstants.SecPerMinute);
  const remaining = Math.floor(seconds % CommonConstants.SecPerMinute);
  return `${minutes}:${String(remaining).padStart(localConstants.durationSecondsPadWidth, localConstants.durationPadChar)}`;
}

/**
 * Determines if a given localization code corresponds to a right‑to‑left (RTL) language, based on locale data.
 *
 * @param locale - The localization code to determine RTL or LTR formatting.
 * @returns True if the locale is RTL, false if LTR. Defaults to false for unknown locales.
 */
export function GetLocaleRTL(locale: LocalizationCode): boolean {
  return GetLocaleData(locale).RTL;
}

/**
 * Converts an item or array of items into an array. If the item is null/undefined, returns an empty array.
 *
 * @param item - An item or array of items, possibly null/undefined.
 * @returns An array containing the item(s), or an empty array if null/undefined.
 */
export function ToArray<T>(item: T | T[] | null | undefined): T[] {
  if (item === null || item === undefined) {
    return [];
  }
  return Array.isArray(item) ? item : [item];
}

/**
 * Shuffles an array using Fisher‑Yates, but respects a mapping of fixed positions.
 *
 * @param array - The array to shuffle (NOT mutated).
 * @param map - IndexFrom → indexTo mapping. These positions are fixed.
 * @returns A new array with mapped items placed first, and the rest shuffled.
 */
export function ShuffleArray<T>(array: readonly T[], map?: Record<number, number>): T[] {
  const result = new Array<T>(array.length);
  const usedTargets = new Set<number>();
  const usedSources = new Set<number>();

  // Place mapped items into their fixed positions
  if (map) {
    for (const fromStr of Object.keys(map)) {
      const from = Number(fromStr);
      const to = map[from];

      // Skip undefined or out‑of‑range mappings
      if (to === undefined) continue;
      if (to < 0 || to >= array.length) continue;
      if (from < 0 || from >= array.length) continue;

      result[to] = array[from];
      usedTargets.add(to);
      usedSources.add(from);
    }
  }

  // Collect all remaining items (not mapped)
  const remaining: T[] = [];
  for (let i = 0; i < array.length; i++) {
    if (!usedSources.has(i)) {
      remaining.push(array[i]);
    }
  }

  // Shuffle the remaining items
  for (let i = remaining.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [remaining[i], remaining[j]] = [remaining[j], remaining[i]];
  }

  // Fill remaining slots in result
  let rIndex = 0;
  for (let i = 0; i < result.length; i++) {
    if (!usedTargets.has(i)) {
      result[i] = remaining[rIndex++];
    }
  }

  return result;
}

/**
 * Replaces indexed placeholders (`{0}`, `{1}`, …) in a localized template string with the provided arguments.
 *
 * @param template - The localized string containing `{0}`, `{1}`, etc.
 * @param args - Values to substitute (converted to string via `String()`).
 * @returns The formatted string with all placeholders replaced.
 */
export function FormatLocalized(template: string, ...args: (string | number)[]): string {
  return template.replace(/\{(\d+)\}/g, (match, index) => {
    const i = Number(index);
    return i < args.length ? String(args[i]) : match;
  });
}

/**
 * Resolves the English display name of a BCP-47 locale's language (e.g. `es-MX` to `Spanish`), for embedding in English-language model prompts. Falls back to the raw locale string when the
 * runtime cannot resolve a name.
 *
 * @param locale - The BCP-47 locale (e.g. `es-MX`, `fr`, `pt-BR`).
 * @returns The English language name, or the original locale string on failure.
 */
export function GetLanguageName(locale: string): string {
  try {
    return new Intl.DisplayNames(['en'], { type: 'language' }).of(locale) ?? locale;
  } catch {
    return locale;
  }
}

/**
 * Wraps a value in Unicode directional isolates (LRI ... PDI) so it always renders left-to-right, even inside an RTL (Arabic/Hebrew) paragraph. Use for phone numbers, IDs, and Latin-script legal
 * names/codes that otherwise visually reverse (e.g. "+1 (778) 907-9409" showing as "907-9409 (778) 1+") when the app direction is RTL. The isolates are invisible; an empty input is returned
 * as-is.
 *
 * @param value - The text to force left-to-right.
 * @returns The value wrapped in LRI/PDI isolates, or an empty string when the input is blank.
 */
export function WrapLTR(value: string | null | undefined): string {
  if (!value) return '';
  return `${localConstants.leftToRightIsolate}${value}${localConstants.popDirectionalIsolate}`;
}

/**
 * Capitalizes the first letter of a string, leaving the rest unchanged.
 *
 * @param value - The input string.
 * @returns The string with its first character uppercased, or the original string if empty.
 */
export function Capitalize(value: string): string {
  if (value.length === 0) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Validates a password against the account password policy (matches the Firebase Identity Platform password policy configured in the Firebase Console — see `CommonConstants.Password`). The
 * unified error message paired with this check is "Password must be {MinLength}-{MaxLength} characters and include at least one number.".
 *
 * @param password - The password to validate.
 * @returns True if the password length is within `[MinLength, MaxLength]` and contains at least one numeric character.
 */
export function ValidatePassword(password: string): boolean {
  if (password.length < CommonConstants.Password.MinLength) return false;
  if (password.length > CommonConstants.Password.MaxLength) return false;
  return /[0-9]/.test(password);
}

/**
 * Derives a default username from a raw input (Firebase `displayName` or the local part of an email). Splits on word boundaries (`.`, `_`, `-`, `+`, whitespace), PascalCases each token, strips
 * characters outside `[A-Za-z0-9]`, prepends the configured fallback if the result does not start with a letter, and clamps to `[CommonConstants.Username.MinLength,
 * CommonConstants.Username.MaxLength]`. Returns `CommonConstants.Username.Fallback` if the cleaned result is shorter than `MinLength`.
 *
 * @param raw - The raw input (display name or email local part).
 * @returns A sanitized username guaranteed to be at most `CommonConstants.Username.MaxLength` chars long, starting with a letter.
 */
export function SanitizeUsername(raw: string): string {
  const fallback = CommonConstants.Username.Fallback;
  if (!raw) return fallback;

  // Split on word boundaries, PascalCase each token, strip non-alphanumerics, concatenate.
  const tokens = raw.split(/[._\-+\s]+/);
  let cleaned = '';
  for (const token of tokens) {
    const stripped = token.replace(/[^A-Za-z0-9]/g, '');
    if (stripped.length === 0) continue;
    cleaned += stripped.charAt(0).toUpperCase() + stripped.slice(1).toLowerCase();
  }

  // Must start with a letter — otherwise prepend the fallback.
  if (cleaned.length === 0 || !/^[A-Za-z]/.test(cleaned)) {
    cleaned = fallback + cleaned;
  }

  // Clamp to the configured limits.
  if (cleaned.length < CommonConstants.Username.MinLength) return fallback;
  if (cleaned.length > CommonConstants.Username.MaxLength) cleaned = cleaned.slice(0, CommonConstants.Username.MaxLength);
  return cleaned;
}

/**
 * Converts a camelCase or PascalCase string into human-readable display text.
 *
 * Splits on word boundaries (uppercase letters and digit transitions), capitalizes each word. Example: `transactionsArchive2025` → `Transactions Archive 2025`, `sessionRecord` → `Session
 * Record`.
 *
 * @param value - The camelCase or PascalCase string.
 * @returns A space-separated, capitalized display string.
 */
export function CamelPascalToDisplayText(value: string): string {
  if (value.length === 0) return value;
  const words = value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .replace(/(\d)([A-Za-z])/g, '$1 $2')
    .split(' ');
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// Campaign

/**
 * Produces a simple 32-bit DJB2 hash of a string, returned as a hex string.
 *
 * @param value - The input string to hash.
 * @returns An 8-character lowercase hex hash.
 */
export function HashString(value: string): string {
  let hash = 5381;
  for (let i = 0; i < value.length; i++) {
    hash = ((hash << 5) + hash + value.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

// Input sanitization

/**
 * Validates that a value is a syntactically valid E.164 phone number (e.g. "+15551234567").
 *
 * @param value - The candidate phone string.
 * @returns True when the value matches the E.164 pattern.
 */
export function IsValidE164(value: string): boolean {
  return typeof value === 'string' && localConstants.e164Pattern.test(value);
}

/**
 * Normalizes a loosely typed phone number into canonical E.164. Strips every non-digit separator (spaces, dashes, dots, parentheses) and applies the country code: an explicit '+' prefix or a
 * leading '00' international prefix keeps the number's own country code, while a bare national number takes `defaultCountryCode` (the North American Numbering Plan, +1, when no default is
 * given). So `2068222207`, `206-822-2207`, `206.822.2207`, `(206) 822-2207`, and `206 822 2207` all become `+12068222207`, and `207 946 0000` becomes `+442079460000` with a `'44'` default.
 * Returns an empty string for empty/undefined input, and best-effort output (a '+' followed by the digits) for anything it cannot confidently place - callers validate the result with
 * {@link IsValidE164} before persisting.
 *
 * @param value - The candidate phone string in any common human format.
 * @param defaultCountryCode - Optional country calling code (digits, no plus) applied to bare national numbers; non-digit content is ignored and degrades to the NANP default.
 * @returns The E.164-normalized number, or an empty string when there is nothing to normalize.
 */
export function NormalizePhoneE164(value: string, defaultCountryCode?: string): string {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (trimmed.length === 0) return '';
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(localConstants.phoneNonDigitPattern, '');
  if (digits.length === 0) return '';
  // An explicit '+' means the country code is already present; just drop the separators.
  if (hasPlus) return `+${digits}`;
  // '00' is the common international dialling prefix (e.g. 00 44 ...) - swap it for '+'.
  if (digits.startsWith(localConstants.phoneInternationalPrefix)) return `+${digits.slice(localConstants.phoneInternationalPrefix.length)}`;
  // A bare national number takes the caller's default country code (digits only - a non-digit default degrades to NANP rather than producing an unplaceable number). An 11-digit number already
  // carrying the '1' country code just needs the '+' (handled by the fallthrough).
  const countryCode = (defaultCountryCode ?? '').replace(localConstants.phoneNonDigitPattern, '') || localConstants.phoneDefaultCountryCode;
  if (digits.length === localConstants.phoneNanpNationalDigits) return `+${countryCode}${digits}`;
  return `+${digits}`;
}

/**
 * Collapses free text to a single line: every run of whitespace (spaces, tabs, and line breaks) becomes one space and the result is trimmed. Used for fields that must never contain line breaks
 * (e.g. the agent greeting, which is spoken as one opening utterance). Returns an empty string for empty/undefined input.
 *
 * @param value - The candidate text.
 * @returns The single-line, whitespace-collapsed text.
 */
export function NormalizeSingleLine(value: string): string {
  if (typeof value !== 'string') return '';
  return value.replace(localConstants.anyWhitespaceRunPattern, ' ').trim();
}

/**
 * Normalizes multi-line free text: line endings are unified to '\n', trailing whitespace is stripped from each line, runs of blank (or whitespace-only) lines collapse to a single blank line, and
 * leading/trailing blank lines are removed. Preserves intentional single blank lines between paragraphs while eliminating double blanks and whitespace-only lines. Used for the call-handling
 * instructions. Returns an empty string for empty/undefined input.
 *
 * @param value - The candidate multi-line text.
 * @returns The normalized text.
 */
export function NormalizeMultiline(value: string): string {
  if (typeof value !== 'string') return '';
  const lines = value
    .replace(localConstants.eolPattern, '\n')
    .split('\n')
    .map((line) => line.replace(localConstants.trailingWhitespacePattern, ''));
  const collapsed: string[] = [];
  for (const line of lines) {
    const blank = line.length === 0;
    // Skip a blank line that immediately follows another blank line so double blanks never survive.
    if (blank && collapsed.length > 0 && collapsed[collapsed.length - 1].length === 0) continue;
    collapsed.push(line);
  }
  while (collapsed.length > 0 && collapsed[0].length === 0) collapsed.shift();
  while (collapsed.length > 0 && collapsed[collapsed.length - 1].length === 0) collapsed.pop();
  return collapsed.join('\n');
}

/**
 * Validates that a value has a syntactically plausible email shape and is within the addressable length cap.
 *
 * @param value - The candidate email string.
 * @returns True when the value looks like an email and is at most `CommonConstants.Validation.EmailMaxLength` chars.
 */
export function IsValidEmail(value: string): boolean {
  return typeof value === 'string' && value.length <= CommonConstants.Validation.EmailMaxLength && localConstants.emailPattern.test(value);
}

/**
 * Validates that a value is a plausible website URL for form input: parseable by the URL constructor (a missing scheme is tolerated by assuming https), with a dotted, space-free hostname and
 * within the shared URL length cap. Deliberately permissive - a format gate for immediate form feedback, not a reachability check.
 *
 * @param value - The candidate URL string (with or without scheme).
 * @returns True when the value parses as a plausible website URL.
 */
export function IsPlausibleURL(value: string): boolean {
  if (typeof value !== 'string' || value.length === 0 || value.length > CommonConstants.Validation.URLMaxLength) return false;
  const candidate = value.includes('://') ? value : `${localConstants.urlDefaultScheme}${value}`;
  try {
    const parsed = new URL(candidate);
    return localConstants.urlHostPattern.test(parsed.hostname);
  } catch {
    return false;
  }
}

/**
 * Validates that a value is a well-formed ISO-8601 combined date/time.
 *
 * Shape is checked against {@link localConstants.iso8601Pattern} BEFORE parsing, then the parse itself rejects out-of-range field values (month 13, day 32). A bare `Date.parse` gate is not
 * sufficient: it also accepts engine-specific formats such as `'Dec 25 2026'` or a lone `'2026'`, which would pass a date validation and then be interpreted differently elsewhere.
 *
 * The zone designator is optional - callers that require an absolute instant must say so themselves.
 *
 * @param value - The candidate date string.
 * @returns True when the value is an ISO-8601 date-time naming a real calendar date and time.
 */
export function IsValidISO8601(value: string): boolean {
  if (typeof value !== 'string' || !localConstants.iso8601Pattern.test(value)) return false;
  return !Number.isNaN(Date.parse(value));
}

/**
 * Validates that a value is a syntactically valid UUID (canonical 8-4-4-4-12 hex form). Used to gate route params bound to PostgreSQL `uuid` columns so a malformed id is rejected as a 400 at the
 * boundary rather than surfacing as a 500 when the database rejects the cast.
 *
 * @param value - The candidate UUID string.
 * @returns True when the value matches the canonical UUID shape.
 */
export function IsValidUUID(value: string | undefined | null): boolean {
  return typeof value === 'string' && localConstants.uuidPattern.test(value);
}

/**
 * Trims surrounding whitespace and truncates a free-text value to a maximum length. Used to bound untrusted free-text fields (names, notes, messages) before they are persisted or forwarded to a
 * provider, rather than rejecting an in-flight action over an oversized string.
 *
 * @param value - The raw text value.
 * @param maxLength - The maximum number of characters to keep.
 * @returns The trimmed value, truncated to `maxLength` characters.
 */
export function ClampText(value: string, maxLength: number): string {
  const trimmed = value.trim();
  return trimmed.length > maxLength ? trimmed.slice(0, maxLength) : trimmed;
}

/**
 * Sanitizes customer-facing free text before it lands on a calendar event: collapse to a single line, strip angle brackets, then length-clamp.
 *
 * Google Calendar renders an event description as HTML, so a raw `<` or `>` arriving from a transcribed name, an SMS body, or an injected utterance could smuggle markup into the tenant's
 * calendar. Every channel that writes customer text to a booking must run it through here — a plain `ClampText` bounds the length but leaves the markup intact.
 *
 * @param value - The raw customer-facing text.
 * @param maxLength - The field's length cap.
 * @returns The sanitized text.
 */
export function SanitizeCalendarText(value: string, maxLength: number): string {
  return ClampText(NormalizeSingleLine(value).replace(localConstants.calendarMarkupCharsPattern, ''), maxLength);
}

/**
 * Masks a phone number for logging: the leading `+` and the last four digits survive, every other digit becomes `*`.
 *
 * Logs are shipped off-box and retained far longer than the tenant-scoped rows the number legitimately lives in, so a raw E.164 in a log line is an uncontrolled copy of customer PII. Four
 * trailing digits are enough for an operator to correlate a log line with a call or conversation record they are already authorised to read, and are not enough to dial or identify anyone.
 * Numbers shorter than the retained tail are masked entirely. An empty input stays empty so call sites can keep their own "anonymous caller" fallback.
 *
 * @param value - The phone number, E.164 or otherwise.
 * @returns The masked number, or an empty string when there is nothing to mask.
 */
export function RedactPhone(value: string | undefined | null): string {
  if (typeof value !== 'string' || value.length === 0) return '';
  const plus = value.startsWith('+') ? '+' : '';
  const digits = value.replace(localConstants.phoneNonDigitPattern, '');
  if (digits.length <= localConstants.redactedPhoneTailDigits) return `${plus}${localConstants.redactionChar.repeat(digits.length)}`;
  return `${plus}${localConstants.redactionChar.repeat(digits.length - localConstants.redactedPhoneTailDigits)}${digits.slice(-localConstants.redactedPhoneTailDigits)}`;
}

/**
 * Masks an email address for logging: the first character of the local part and the whole domain survive, the rest of the local part becomes `*`.
 *
 * Same reasoning as {@link RedactPhone} - the domain is what operators actually triage on (tenant, provider, disposable-mail abuse), while the local part is the identifying half. A value that
 * does not contain an `@` is treated as a bare local part and masked whole. An empty input stays empty.
 *
 * @param value - The email address.
 * @returns The masked address, or an empty string when there is nothing to mask.
 */
export function RedactEmail(value: string | undefined | null): string {
  if (typeof value !== 'string' || value.length === 0) return '';
  const at = value.lastIndexOf('@');
  const local = at === -1 ? value : value.slice(0, at);
  const domain = at === -1 ? '' : value.slice(at);
  if (local.length <= 1) return `${localConstants.redactionChar}${domain}`;
  return `${local[0]}${localConstants.redactionChar.repeat(local.length - 1)}${domain}`;
}

/**
 * Appends a sender attribution line to an SMS or email body so the recipient knows the message is from Vertex Echo and, when available, on behalf of the business.
 *
 * @param body - The original message body.
 * @param businessName - The tenant's business name; omit or pass empty to fall back to the platform name only.
 * @returns The body with a trailing attribution line separated by a blank line.
 */
export function AppendSenderAttribution(body: string, businessName?: string): string {
  const name = businessName?.trim();
  // Attribute on behalf of the business only when a name is present and it does not already contain the platform name - "on behalf of" an Echo-named business is redundant.
  const onBehalf = name && !localConstants.platformNamePattern.test(name);
  const attribution = onBehalf ? `${localConstants.platformAttribution}, on behalf of ${name}.` : `${localConstants.platformAttribution}.`;
  return `${body}\n\n${attribution}`;
}

/**
 * Sanitizes the staged onboarding `state` from the public, unauthenticated signup endpoint before it is persisted to Mongo.
 *
 * The wire type is `Record<string, string>`, but the body is attacker-controlled JSON at runtime, so the shape is enforced, not trusted. Only own, string-valued entries survive; each key is
 * trimmed and length-capped; keys beginning with `$`, containing `.`, or matching a prototype sentinel are dropped to defeat Mongo operator, dotted-path, and prototype-pollution injection; every
 * value is clamped with `ClampText`; and both the key count and a total character budget are bounded so one prospect cannot bloat the document. Returns a fresh flat object safe to spread.
 *
 * @param state - The raw, untrusted staged-form map from the request body.
 * @returns A new flat `Record<string, string>` containing only the entries that passed every check.
 */
export function SanitizeSignupState(state: unknown): Record<string, string> {
  const sanitized: Record<string, string> = {};
  if (typeof state !== 'object' || state === null) return sanitized;

  let totalChars = 0;
  for (const [rawKey, rawValue] of Object.entries(state as Record<string, unknown>)) {
    if (Object.keys(sanitized).length >= CommonConstants.Validation.SignupStateMaxKeys) break;
    if (typeof rawValue !== 'string') continue;

    const key = rawKey.trim();
    if (key.length === 0 || key.length > CommonConstants.Validation.SignupStateKeyMaxLength) continue;
    if (key.startsWith(localConstants.mongoOperatorKeyPrefix) || key.includes(localConstants.mongoDottedPathChar) || (localConstants.forbiddenObjectKeys as readonly string[]).includes(key)) continue;

    const value = ClampText(rawValue, CommonConstants.Validation.SignupStateValueMaxLength);
    if (totalChars + key.length + value.length > CommonConstants.Validation.SignupStateMaxTotalChars) break;

    totalChars += key.length + value.length;
    sanitized[key] = value;
  }

  return sanitized;
}

/**
 * Sanitizes the free-form `metadata` bag of a log entry arriving on the public, unauthenticated log endpoint before it is persisted to Mongo.
 *
 * The wire type declares scalar values, but the body is attacker-controlled JSON at runtime, so the shape is enforced, not trusted. Only own entries with a scalar (string/number/boolean/null)
 * value survive; keys beginning with `$`, containing `.`, or matching a prototype sentinel are dropped to defeat Mongo operator, dotted-path, and prototype-pollution injection; non-finite
 * numbers are dropped (they do not survive BSON round-tripping); string values are clamped; and both the key count and a total character budget are bounded so one client cannot bloat the
 * document. Returns a fresh flat object safe to spread.
 *
 * @param metadata - The raw, untrusted metadata bag from the request body.
 * @returns A new flat scalar map containing only the entries that passed every check.
 */
export function SanitizeLogMetadata(metadata: unknown): Record<string, string | number | boolean | null> {
  const sanitized: Record<string, string | number | boolean | null> = {};
  if (typeof metadata !== 'object' || metadata === null) return sanitized;

  let totalChars = 0;
  for (const [rawKey, rawValue] of Object.entries(metadata as Record<string, unknown>)) {
    if (Object.keys(sanitized).length >= CommonConstants.Validation.LogMetadataMaxKeys) break;

    const key = rawKey.trim();
    if (key.length === 0 || key.length > CommonConstants.Validation.LogMetadataKeyMaxLength) continue;
    if (key.startsWith(localConstants.mongoOperatorKeyPrefix) || key.includes(localConstants.mongoDottedPathChar) || (localConstants.forbiddenObjectKeys as readonly string[]).includes(key)) continue;

    let value: string | number | boolean | null;
    if (typeof rawValue === 'string') value = ClampText(rawValue, CommonConstants.Validation.LogMetadataValueMaxLength);
    else if (typeof rawValue === 'number') {
      if (!Number.isFinite(rawValue)) continue;
      value = rawValue;
    } else if (typeof rawValue === 'boolean' || rawValue === null) value = rawValue;
    else continue;

    // Non-string scalars are stored as fixed-width BSON values, so they only need to count against the key count, not the character budget.
    const valueChars = typeof value === 'string' ? value.length : 0;
    if (totalChars + key.length + valueChars > CommonConstants.Validation.LogMetadataMaxTotalChars) break;

    totalChars += key.length + valueChars;
    sanitized[key] = value;
  }

  return sanitized;
}

/**
 * Reports whether an untrusted value is a supported LocalizationCode. Use at strict boundaries (e.g. the gated translation endpoint) where an unrecognized language must be rejected rather than
 * silently defaulted.
 *
 * @param value - The raw, untrusted language value.
 * @returns True when the value is a known LocalizationCode.
 */
export function IsValidLocalizationCode(value: string | undefined | null): boolean {
  return !!value && (LocalizationCodeValues as readonly string[]).includes(value);
}

/**
 * Normalizes an untrusted `lang` value to a supported LocalizationCode, falling back to the default localization when the value is missing or unrecognized. Public endpoints forward this value
 * into a Redis cache-key segment and an LLM translation target, so normalizing it here prevents cache-key pollution and prompt steering via an arbitrary attacker-supplied string.
 *
 * @param value - The raw, untrusted language value (query string, header, or body field).
 * @returns A supported LocalizationCode.
 */
export function NormalizeLocalizationCode(value: string | undefined | null): LocalizationCode {
  if (value && (LocalizationCodeValues as readonly string[]).includes(value)) return value;
  return CommonConstants.Defaults.Localization;
}

/**
 * Builds the idempotent `messages` usage-meter job for a single submitted notification. Both channels (SMS and email) ride this meter — they each cost us per send. One unit is recorded per
 * notification; the idempotency key (`<tenantID>:<notificationID>:messages`) guards against double-counting when a send path is retried for the same notification row. Shared by the automation
 * and voice services so both emit an identically shaped job.
 *
 * @param params - The owning tenant, the notification row id (the usage source), and the ISO-8601 time the notification was submitted to the provider.
 * @param params.tenantID - The tenant that owns the notification and is billed for the unit.
 * @param params.notificationID - The notification row id, used as both the usage source id and part of the idempotency key.
 * @param params.occurredAtISO - The ISO-8601 time the notification was submitted to the provider.
 * @returns The usage-meter job to enqueue.
 */
export function BuildNotificationUsageJob(params: { tenantID: string; notificationID: string; occurredAtISO: string }): IUsageMeterJob {
  const meterKey = CommonConstants.Database.MeterKey.Messages;
  return {
    tenantID: params.tenantID,
    meterKey,
    quantity: localConstants.notificationUsageQuantity,
    sourceType: localConstants.notificationUsageSource,
    sourceID: params.notificationID,
    idempotencyKey: [params.tenantID, params.notificationID, meterKey].join(localConstants.usageKeySeparator),
    occurredAtISO: params.occurredAtISO
  };
}

/**
 * Builds the idempotent `messages` usage-meter job for a single RECEIVED customer text (the "in" half of the meter's in-and-out coverage; outbound texts ride the notification-ledger job above).
 * One unit per inbound message; the idempotency key (`<tenantID>:<textMessageID>:messages`) guards against double-counting when the ingest job is retried, and the processor only builds this job
 * when it actually creates the inbound row (a replayed delivery reuses the stored row and emits nothing).
 *
 * @param params - The owning tenant, the inbound text-message row id (the usage source), and the ISO-8601 time the message was received.
 * @param params.tenantID - The tenant that owns the conversation and is billed for the unit.
 * @param params.messageID - The inbound `text_messages` row id, used as both the usage source id and part of the idempotency key.
 * @param params.occurredAtISO - The ISO-8601 time the message was received.
 * @returns The usage-meter job to enqueue.
 */
export function BuildTextMessageUsageJob(params: { tenantID: string; messageID: string; occurredAtISO: string }): IUsageMeterJob {
  const meterKey = CommonConstants.Database.MeterKey.Messages;
  return {
    tenantID: params.tenantID,
    meterKey,
    quantity: localConstants.notificationUsageQuantity,
    sourceType: localConstants.textMessageUsageSource,
    sourceID: params.messageID,
    idempotencyKey: [params.tenantID, params.messageID, meterKey].join(localConstants.usageKeySeparator),
    occurredAtISO: params.occurredAtISO
  };
}

/**
 * Converts a colon-separated idempotency key into a BullMQ-safe custom job id. BullMQ REJECTS custom job ids containing `:` ("Custom Id cannot contain :" - the colon is its Redis key separator),
 * while the platform's durable idempotency keys are deliberately colon-joined (`<tenantID>:<callID>:<meterKey>`). Use this at every `queue.add(..., { jobId })` call site; the durable Postgres
 * columns keep the original colon form.
 *
 * @param idempotencyKey - The colon-separated idempotency key.
 * @returns The same key with colons replaced by hyphens, safe to use as a BullMQ job id.
 */
export function ToJobID(idempotencyKey: string): string {
  return idempotencyKey.split(localConstants.usageKeySeparator).join(localConstants.jobIDSeparator);
}

/**
 * Neutralises a value for safe inclusion in a CSV cell. Doubles embedded quotes (RFC 4180) and prefixes a single quote when the value begins with a character a spreadsheet would interpret as a
 * formula (= + - @ tab CR), defeating CSV formula-injection.
 *
 * @param value - The raw field value.
 * @returns The escaped, injection-safe field value.
 */
export function SanitizeCSVField(value: string): string {
  const escaped = value.replace(/"/g, '""');
  return localConstants.csvFormulaPrefixPattern.test(escaped) ? `'${escaped}` : escaped;
}

/**
 * Coerces an untrusted pagination `limit` into a safe page size. Non-numeric, NaN, or sub-1 values fall back to the default page size; anything above the configured ceiling is clamped down to
 * it. Guarantees the caller always receives a positive integer within `[1, CommonConstants.Pagination.MaxLimit]`.
 *
 * @param value - The raw limit value from the request (string, number, or undefined).
 * @returns A bounded positive integer page size.
 */
export function ClampLimit(value: unknown): number {
  const parsed = Math.floor(Number(value));
  if (!Number.isFinite(parsed) || parsed < 1) return CommonConstants.Pagination.Limit;
  return Math.min(parsed, CommonConstants.Pagination.MaxLimit);
}

/**
 * Coerces an untrusted pagination `offset` into a safe, non-negative integer. Non-numeric, NaN, or negative values fall back to the default offset.
 *
 * @param value - The raw offset value from the request (string, number, or undefined).
 * @returns A bounded non-negative integer offset.
 */
export function ClampOffset(value: unknown): number {
  const parsed = Math.floor(Number(value));
  if (!Number.isFinite(parsed) || parsed < 0) return CommonConstants.Pagination.Offset;
  return parsed;
}

/**
 * Determines whether a user has active SMS consent.
 *
 * Consent is valid when `smsConsentAcceptedAt` exists and is later than `smsOptedOutAt` (so re-consent after an opt-out is honoured).
 *
 * @param user - An object bearing optional `smsConsentAcceptedAt` and `smsOptedOutAt` dates.
 * @param user.smsConsentAcceptedAt
 * @param user.smsOptedOutAt
 * @returns True when the user has an active SMS consent.
 */
export function HasSmsConsent(user: { smsConsentAcceptedAt?: Date | null; smsOptedOutAt?: Date | null }): boolean {
  if (!user.smsConsentAcceptedAt) return false;
  if (!user.smsOptedOutAt) return true;
  return user.smsConsentAcceptedAt > user.smsOptedOutAt;
}

/**
 * Normalizes raw extracted document text (TXT decode or PDF parse output) into clean, storable plain text. Strips non-printable control characters and common PDF artifacts (NUL, form feed,
 * zero-width and soft-hyphen glyphs), normalizes line endings, trims trailing whitespace from each line, collapses runs of spaces/tabs to a single space, and collapses three-or-more consecutive
 * blank lines down to a single blank line. The result is trimmed overall.
 *
 * @param value - The raw extracted text.
 * @returns The cleaned plain text.
 */
export function CleanExtractedText(value: string): string {
  return (
    value
      // Normalize CRLF and lone CR to LF.
      .replace(/\r\n?/g, '\n')
      // Drop NUL, form feed, and other C0/C1 control chars except tab and newline. Matching control characters is the whole point here, so the no-control-regex rule is intentionally disabled.
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g, '')
      // Drop zero-width and soft-hyphen glyphs that PDF extraction frequently injects.
      .replace(/[\u00AD\u200B-\u200D\uFEFF]/g, '')
      // Collapse runs of spaces and tabs to a single space.
      .replace(/[ \t]+/g, ' ')
      // Trim trailing spaces at the end of each line.
      .replace(/ +\n/g, '\n')
      // Trim leading spaces at the start of each line.
      .replace(/\n +/g, '\n')
      // Collapse three-or-more newlines (two-or-more blank lines) into a single blank line.
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  );
}

/**
 * Derives a human-friendly document title from an uploaded file name. Drops the directory path and trailing extension, splits the remaining stem on separators (`.`, `_`, `-`, whitespace),
 * capitalizes each token, and joins them with single spaces - producing the kind of title a person would type by hand. Returns an empty string when nothing usable remains (callers supply their
 * own default).
 *
 * @param filename - The original uploaded file name (e.g. `my-product_guide.v2.pdf`).
 * @returns The derived title (e.g. `My Product Guide V2`), or an empty string when none can be derived.
 */
export function DeriveTitleFromFilename(filename: string): string {
  if (!filename) return '';
  // Keep only the base name, then strip a single trailing extension.
  const base = filename.split(/[\\/]/).pop() ?? '';
  const stem = base.replace(/\.[^.]+$/, '');
  return stem
    .split(/[._\-\s]+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0)
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ');
}
